"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const debounce_1 = __importDefault(require("lodash/debounce"));
const button_1 = require("@kyruus/button");
const utils_1 = require("./utils");
const use_on_change_1 = require("./use-on-change");
const error_list_1 = __importDefault(require("./error-list"));
const row_1 = __importDefault(require("../fields/rows/row"));
const use_form_1 = require("./use-form");
const styles_1 = require("../styles");
const field_list_1 = __importDefault(require("../fields/field-list"));
const constants_1 = require("../constants");
const get_derived_state_1 = __importDefault(require("./get-derived-state"));
const DefaultSectionHeader_1 = require("../components/DefaultSectionHeader");
/**
 * FormContainer is responsible for:
 *
 * - Initializing FormContext.Provider
 * - Creating all action handlers
 * - Extracting any components passed via props
 * - Handling side effects
 */
const FormContainer = (props) => {
    const isInitialized = (0, react_1.useRef)(false);
    const [formContext, setFormContext] = (0, react_1.useState)(() => (0, get_derived_state_1.default)(props));
    const { fields, allErrorsSet, isSubmitting, updatedFields } = formContext;
    /**
     * Keep a reference to the updatedFields object outside of context,
     * so that changes do not trigger rerenders.
     */
    const updatedFieldsRef = (0, react_1.useRef)(updatedFields);
    const { isReadOnly, entityData, submitButtonText, submitButtonDataTestId, hideDefaultSubmit, errorHeaderText, label, fieldAddOns = emptyObject, formAddOns: { Footer, Header }, scrollOnError, formRef, customRows, customInlineRows, fieldSections, SectionHeaderComponent = DefaultSectionHeader_1.DefaultSectionHeader } = props;
    /**
     * Setup action handlers
     */
    const getErrors = (0, utils_1.getGetErrors)({ entityData, fields, updatedFieldsRef });
    const removePaths = (0, utils_1.getRemovePaths)({
        updatedFieldsRef,
        setFormContext
    });
    const renderErrors = (0, utils_1.getRenderErrors)(getErrors, updatedFieldsRef, setFormContext);
    const { handleChangeWithTracking } = (0, use_on_change_1.useOnChange)({
        updatedFieldsRef,
        getErrors,
        props,
        formContext,
        setFormContext,
        renderErrors
    });
    const toggleEditMode = (0, utils_1.getToggleEditMode)({ formContext, setFormContext });
    const handleSubmit = (0, utils_1.getHandleSubmit)({
        getErrors,
        updatedFieldsRef,
        setFormContext,
        formContext,
        props
    });
    // see onChange prop for why this is needed
    const onSubmitDebounce = (0, react_1.useCallback)((0, debounce_1.default)(handleSubmit, constants_1.DEBOUNCE_TIME + constants_1.DEBOUNCE_FORM_OFFSET), [handleSubmit]);
    const handleOnSubmit = (0, react_1.useCallback)((e) => {
        // Need to immediately prevent the event to stop default form submission behavior
        e.preventDefault();
        onSubmitDebounce(e);
    }, [onSubmitDebounce]);
    /**
     * Extract components from props
     */
    if (fieldAddOns !== formContext.addOns) {
        setFormContext((state) => {
            return Object.assign(Object.assign({}, state), { addOns: fieldAddOns });
        });
    }
    /**
     * Pass isValid, renderErrors and updatedFields to props.onChange on initial render.
     */
    (0, react_1.useEffect)(() => {
        if (!props.onChange || isInitialized.current)
            return;
        const isValid = !getErrors().allErrorsSet.size;
        props.onChange({
            isValid,
            renderErrors,
            updatedFields: formContext.updatedFields
        });
    }, [props, formContext, getErrors, renderErrors]);
    /**
     * Scroll to top of form when new errors are present
     */
    const refToFormTop = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        if (!scrollOnError)
            return;
        isInitialized.current && (0, utils_1.scrollToRef)(refToFormTop);
    }, [allErrorsSet, scrollOnError]);
    /**
     * Memoize the value provided to FormContext.Provider, which is
     * consumed by useForm.
     */
    const context = {
        context: Object.assign(Object.assign({}, formContext), { removePaths, handleChange: handleChangeWithTracking }),
        setContext: setFormContext
    };
    (0, react_1.useEffect)(() => {
        isInitialized.current = true;
    }, []);
    /**
     * fields can be grouped into sections with headers. sectionIdentifiers of fields have to match
     * sectionIdentifiers of fieldSections. example partial form config:
     * {
     *   fieldSections: [
     *     {
     *       title: 'Patient Details'
     *       sectionIdentifier: 'patientDetails'
     *     },
     *     ...
     *   ],
     *   fields: [
     *     {
     *       type:'name',
     *       sectionIdentifier: 'patientDetails',
     *       ...
     *     }
     *   ]
     * }
     */
    const sectionedFields = fieldSections === null || fieldSections === void 0 ? void 0 : fieldSections.map((fieldSection) => {
        const { title, sectionIdentifier } = fieldSection;
        const filteredFields = fields.filter((field) => field.sectionIdentifier === sectionIdentifier);
        if (filteredFields) {
            return (react_1.default.createElement(styles_1.StyledFieldSection, { key: `${title}:${sectionIdentifier}` },
                react_1.default.createElement(SectionHeaderComponent, null, title),
                react_1.default.createElement(field_list_1.default, { fields: filteredFields, customRows: customRows, customInlineRows: customInlineRows })));
        }
        else {
            return null;
        }
    });
    // anything that doesn't fall into a section (legacy configs or misconfigured forms
    // should end up here and will get rendered below the sections
    const unsectionedFields = fields.filter((field) => {
        const { sectionIdentifier } = field;
        return (!sectionIdentifier ||
            !(fieldSections === null || fieldSections === void 0 ? void 0 : fieldSections.find((fieldSection) => fieldSection.sectionIdentifier === sectionIdentifier)));
    });
    return (react_1.default.createElement(use_form_1.FormContext.Provider, { value: context },
        react_1.default.createElement(error_list_1.default, { errors: Array.from(allErrorsSet), headerText: errorHeaderText, refToFormTop: refToFormTop }),
        react_1.default.createElement("form", { "aria-label": label, method: "post", onSubmit: handleOnSubmit, noValidate: true, ref: formRef },
            Header && (react_1.default.createElement(row_1.default, null,
                react_1.default.createElement(Header, { toggleEditMode: toggleEditMode, handleSubmit: onSubmitDebounce }))),
            sectionedFields,
            unsectionedFields && (react_1.default.createElement(field_list_1.default, { fields: unsectionedFields, customRows: customRows, customInlineRows: customInlineRows })),
            Footer && (react_1.default.createElement(row_1.default, null,
                react_1.default.createElement(Footer, { toggleEditMode: toggleEditMode, handleSubmit: onSubmitDebounce }))),
            !hideDefaultSubmit && !isReadOnly && (react_1.default.createElement(row_1.default, null,
                react_1.default.createElement(styles_1.SubmitWrapper, { breakpoints: props.breakpoints },
                    react_1.default.createElement(button_1.ButtonWithSpinner, { disabled: props.disableSubmitWhenError
                            ? formContext.isValid === false || isSubmitting
                            : isSubmitting, showSpinner: isSubmitting, type: "submit", mode: "primary", "data-testid": submitButtonDataTestId }, submitButtonText)))))));
};
FormContainer.defaultProps = {
    submitButtonText: 'Submit',
    entityData: {},
    isReadOnly: false,
    breakpoints: {},
    defaultComponents: null,
    errorHeaderText: constants_1.FORM_ERRORS_HEADER,
    hideDefaultSubmit: false,
    onChange: null,
    onSubmit: null,
    label: 'form',
    scrollOnError: true,
    disableSubmitWhenError: false
};
exports.default = FormContainer;
const emptyObject = {};
//# sourceMappingURL=container.js.map